<template>
  <div>

    <div class="flex flex-wrap justify-center border-t border-b py-6 border-theme-200 light:border-theme-600">
      <div class="w-full lg:w-2/3 text-center">
        <easy-date v-model="easyDateValue" :identifier.sync="easyIdentifier"></easy-date>
      </div>
      <div class="w-full lg:w-2/3">
        <div class="flex mt-4 items-center">
          <div class="h-10 px-4 py-2 rounded-l bg-red-500 text-white border border-r-0 border-theme-200 light:border-theme-600 focus:outline-none" @click="clear">
            <i class="fas fa-times"></i>
          </div>
          <flat-pickr
            v-model="dateRange.value"
            :config="dateRange.config.flatpickrConfig"
            class="form-control form-no-outline h-10 border-r-0 rounded-none"
            placeholder="Select date range"
            id="date-range"
            name="date_range"
          ></flat-pickr>
          <button
            type="button"
            class="h-10 px-5 flex items-center border border-purple-400 light:border-purple-600 rounded-r focus:outline-none hover:bg-purple-800 hover:border-purple-700"
            :class="dateRange.value ? 'bg-purple-500 text-white' : ''"
            @click="search"
          >
            <i class="fas fa-search"></i>
            <span class="ml-2">Search</span>
          </button>
        </div>
      </div>
    </div>

    <div class="box border border-theme-600 bg-theme-500 p-4">
      <div class="flex flex-wrap">
        <div class="w-full md:w-1/2">
          <img class="h-10" :src="!config.logoPrintPath || url.resolve(config.URL.API.host, config.logoPrintPath)">
        </div>
        <div class="w-full md:w-1/2 md:text-right">
          <p class="text-2xl font-bold">
            Statement of account
          </p>
        </div>
      </div>

      <div class="flex flex-wrap">
        <div class="w-full md:w-8/12" v-if="contact">
          <div class="mt-3 pb-3 border-b border-theme-200 light:border-theme-600">
            <a class="text-link" @click.stop="$router.push($contactPageByType(contact, 'details'))">
              <span class="mb-0 text-lg">
                {{ contact.first_name }} {{ contact.last_name }}
                <template v-if="contact.company_name"> ({{ contact.company_name }})</template>
                <small>#{{ contact.id }}</small>
              </span>
            </a>
          </div>

          <div class="flex flex-wrap text-sm mt-2">
            <div class="w-full md:w-1/2">
              <div v-for="(addr, i) in contact.addresses" :key="'address-' + i">
                <p class="text-lg font-bold">Address</p>
                <address>
                  {{ addr.address_1 }},<br>
                  {{ addr.city }},
                  <span v-if="addr.state">{{ addr.state }}, </span>
                  <span>{{ addr.post_code }}</span>,<br>
                  <span>{{ CountryByID(addr.country_id).name }}</span>
                </address>
              </div>
            </div>
            <div class="w-full md:w-1/2">
              <p class="text-lg font-bold">Phone</p>
              <p class="font-bold" v-for="(phone, i) in contact.phone_numbers" :key="'phone-' + i">
                <template v-if="phone.countryData = CountryByID(phone.country_id)">
                  <span :title="phone.countryData.name">{{ '+' + phone.countryData.calling_code }}</span> {{ phone.number }}
                </template>
              </p>
            </div>
          </div>
        </div>

        <div class="w-full md:w-4/12 flex flex-col">
          <div class="self-end pb-3 border-b border-theme-200 light:border-theme-600 text-sm">
            <table>
              <tbody>
                <tr>
                  <td>Date:</td>
                  <td class="text-right">
                    {{ dateRange.value }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="text-sm text-right mt-2">
            <p class="text-base font-bold">{{ config.companyName }}</p>
            <p>{{ config.companyAddress }}, {{ config.companyCity }}</p>
            <p v-if="config.companyState">{{ config.companyState }}, </p>
            <p>{{ config.companyPostCode }}, {{ config.companyCountry }}</p>
            <p>{{ config.companyPhone }}</p>
            <p v-if="config.companyVATNumber"><strong>VAT:</strong> {{ config.companyVATNumber }}</p>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <label class="font-bold flex items-center">
          <input class="mr-2" type="checkbox" v-model="showReceipts">
          <span class="text-sm">Show receipts</span>
        </label>
      </div>

      <div class="overflow-x-auto">
        <table class="table table-bordered w-full">
          <thead>
            <tr>
              <th style="width: 20%">Date</th>
              <th style="width: 40%">Description</th>
              <th style="width: 10%">Total</th>
              <th style="width: 10%">Paid</th>
              <th style="width: 10%">Remaining</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="statement.carried.total || statement.carried.paid || statement.carried.remaining">
              <td colspan="2">Carried forward</td>
              <td class="text-right">{{ statement.carried.total | formatMoneyFixed }}</td>
              <td class="text-right">{{ statement.carried.paid | formatMoneyFixed }}</td>
              <td class="text-right">{{ statement.carried.remaining | formatMoneyFixed }}</td>
            </tr>
            <tr v-for="(item, i) in statement.items" :key="'statement-items-' + i">

              <template v-if="item.model === 'invoice'">
                <td class="text-sm">{{ item.created_at }}</td>
                <td>
                  <router-link class="text-link" :to="'/invoice/preview/' + item.id">
                    {{ item.type | sentenceCase }}
                    invoice
                    #{{ item.id }}
                  </router-link>
                </td>
                <td class="text-right">{{ item.total.sub - item.total.discount + item.total.tax | formatMoneyFixed }}</td>
                <td class="text-right">{{ item.total.paid | formatMoneyFixed }}</td>
                <td class="text-right">{{ item.total.remaining | formatMoneyFixed }}</td>
              </template>

              <template v-if="item.model === 'credit_note'">
                <td class="text-sm">{{ item.created_at }}</td>
                <td>
                  <router-link class="text-link" :to="'/credit-note/preview/' + item.id">
                    {{ item.type | sentenceCase }}
                    credit note
                    #{{ item.id }}
                  </router-link>
                </td>
                <td class="text-right">{{ -(item.total.sub - item.total.discount + item.total.tax) | formatMoneyFixed }}</td>
                <td class="text-right">{{ -item.total.paid | formatMoneyFixed }}</td>
                <td class="text-right">{{ -item.total.remaining | formatMoneyFixed }}</td>
              </template>

              <template v-if="item.model === 'receipt' && showReceipts">
                <td class="text-sm">{{ item.created_at }}</td>
                <td>
                  <router-link
                    class="text-orange"
                    :to="'/receipt/details/' + item.id"
                  >
                    Receipt #{{ item.id }}
                  </router-link>
                  for
                  <div v-if="item.payment.invoice">
                    <router-link
                      class="text-link"
                      :to="'/invoice/preview/' + item.payment.invoice.id"
                    >
                      {{ item.payment.invoice.type }}
                      invoice
                      #{{ item.payment.invoice.id }}
                    </router-link>
                  </div>
                  <div v-if="item.payment.credit_note">
                    <router-link
                      class="text-link"
                      :to="'/credit-note/preview/' + item.payment.credit_note.id"
                    >
                      {{ item.payment.credit_note.type }}
                      credit note
                      #{{ item.payment.credit_note.id }}
                    </router-link>
                  </div>
                  <div v-if="item.invoices.length">
                    {{ item.invoices[0].type }} invoices: [
                    <span
                      v-for="(invoice, i) in item.invoices"
                      :key="'invoice-links-' + i"
                    >
                      <router-link
                        class="text-link inline-block"
                        :to="'/invoice/preview/' + invoice.id"
                      >
                        #{{ invoice.id }}
                      </router-link>
                      <span class="inline-block">
                        {{ i !== item.invoices.length - 1 ? ',&nbsp;' : '' }}
                      </span>
                    </span>
                    ]
                  </div>
                  <div v-if="item.credit_notes.length">
                    {{ item.credit_notes[0].type }} credit notes: [
                    <span
                      v-for="(credit_note, i) in item.credit_notes"
                      :key="'credit-note-links-' + i"
                    >
                      <router-link
                        class="text-link inline-block"
                        :to="'/credit-note/preview/' + credit_note.id"
                      >
                        #{{ credit_note.id }}
                      </router-link>
                      <span class="inline-block">
                        {{ i !== item.credit_notes.length - 1 ? ',&nbsp;' : '' }}
                      </span>
                    </span>
                    ]
                  </div>
                </td>
                <td></td>
                <td class="text-right">
                  <p class="text-red-500 font-bold text-sm" v-if="item.gross">GROSS</p>
                  <span v-if="item.payment.credit_note">{{ $formatMoney(-item.payment.value) }}</span>
                  <span v-else>{{ $formatMoney(item.payment.value) }}</span>
                </td>
                <td></td>
              </template>

            </tr>
          </tbody>
          <tfoot class="text-lg font-bold" v-if="statement.items.length">
            <tr>
              <td colspan="2">Totals</td>
              <td class="text-right">
                {{ Math.round(statement.carried.total + total) | formatMoneyFixed }}
              </td>
              <td class="text-right">
                {{ Math.round(statement.carried.paid + statement.paid) | formatMoneyFixed }}
              </td>
              <td class="text-right">
                {{ Math.round(statement.carried.remaining + totalRemaining) | formatMoneyFixed }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="flex flex-wrap flex-col mt-8">
        <div class="self-end text-right">
          <div class="btn px-3 py-2 btn-blue ml-3" @click="print">
            <i class="fas fa-print"></i>
            <span class="ml-2">Print</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import url from 'url'
import axios from 'axios'
import { mapGetters } from 'vuex'
const EasyDate = resolve => require(['@/components/EasyDate'], resolve)

var pdfMake = require('pdfmake/build/pdfmake.js')
var pdfFonts = require('pdfmake/build/vfs_fonts.js')
pdfMake.vfs = pdfFonts.pdfMake.vfs

export default {
  props: {
    contactID: {},
    contactType: {
      type: String,
      default: 'customer'
    },
  },
  components: {
    EasyDate,
  },
  data() {
    return {
      url,
      contact: {},
      statement: {
        carried: {
          remaining: 0,
          paid: 0,
        },
        total: 0,
        paid: 0,
        items: [],
      },
      showReceipts: true,
      easyDateValue: '',
      easyIdentifier: 'current-month',
      dateRange: {
        value: '',
        config: {
          flatpickrConfig: {}
        }
      },
    }
  },
  watch: {
    easyDateValue(dates) {
      if (!dates.from || !dates.to) {
        return
      }
      this.dateRange.value = dates.from.format(this.config.dateFormats.display) + ' to ' + dates.to.format(this.config.dateFormats.display)
      if (!this.contact.id) {
        this.get()
      } else {
        this.getStatement()
      }
    }
  },
  computed: {
    total() {
      return this.statement.items.filter(i => i.model !== 'receipt').map(e => e.model === 'credit_note' ? -(e.total.sub - e.total.discount + e.total.tax) : (e.total.sub - e.total.discount + e.total.tax)).reduce((a, b) => a + b, 0)
    },
    totalRemaining() {
      return this.statement.items.filter(i => i.model !== 'receipt').map(e => e.model === 'credit_note' ? -e.total.remaining : e.total.remaining).reduce((a, b) => a + b, 0)
    },
    ...mapGetters({
      CountryByID: 'countries/countryByID',
      config: 'config/config',
    })
  },
  created() {
    this.dateRange.config.flatpickrConfig = Object.assign({}, this.config.flatpickrConfig)
    this.dateRange.config.flatpickrConfig.mode = 'range'
    this.dateRange.config.flatpickrConfig.static = false
  },
  mounted() {
    this.$EventBus.$on('mass-settle.settled', this.getStatement)
  },
  destroyed() {
    this.$EventBus.$off('mass-settle.settled')
  },
  methods: {
    clear() {
      this.dateRange.value = ''
      this.easyIdentifier = 'current-month'
    },
    search() {
      this.easyIdentifier = ''
      this.getStatement()
    },
    async get() {
      Promise.all([await this.getContact(), await this.getStatement()])
    },
    getContact() {
      return new Promise((resolve, reject) => {
        axios.get(this.contactType + 's/' + this.contactID)
          .then(response => {
            this.contact = Object.assign({}, response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    getStatement() {
      return new Promise((resolve, reject) => {
        let dateRange = this.$getRangeDates(this.dateRange.value, this.config)
        // get statement of account
        axios.post('reports/statement-of-account', {
          contactID: this.contactID,
          from: dateRange.from,
          to: dateRange.to,
          // date range
        })
          .then(response => {
            const data = response.data
            this.statement.total = 0
            this.statement.paid = 0
            this.statement.carried.total = 0
            this.statement.carried.paid = 0
            this.statement.carried.remaining = 0

            // handle carried forward history
            if (data.history.credit_notes && data.history.credit_notes.length) {
              for (let i in data.history.credit_notes) {
                let history = data.history.credit_notes[i]
                this.statement.carried.total -= parseFloat(history.total_items)
                if (!history.is_settled) {
                  this.statement.carried.remaining -= parseFloat(history.total_items) - parseFloat(history.total_payments)
                }
              }
            }

            if (data.history.invoices && data.history.invoices.length) {
              for (let i in data.history.invoices) {
                let history = data.history.invoices[i]
                this.statement.carried.total += parseFloat(history.total_items)
                if (!history.is_settled) {
                  this.statement.carried.remaining += parseFloat(history.total_items) - parseFloat(history.total_payments)
                }
              }
            }
            if (data.history.receipts && data.history.receipts.length) {
              for (let i in data.history.receipts) {
                let history = data.history.receipts[i]
                if (history.type === 'income') {
                  this.statement.carried.paid += parseFloat(history.total)
                } else if (history.type === 'expense') {
                  this.statement.carried.paid -= parseFloat(history.total)
                }
              }
            }

            let items = []
            for (let i in data.invoices) {
              let invoice = data.invoices[i]
              this.$setInvoiceTotals(invoice)
              invoice.model = 'invoice'
              items.push(invoice)
            }
            for (let i in data.credit_notes) {
              let creditNote = data.credit_notes[i]
              this.$setInvoiceTotals(creditNote)
              creditNote.model = 'credit_note'
              items.push(creditNote)
            }
            for (let i in data.receipts) {
              let grossTotal = data.receipts[i].invoices.length + data.receipts[i].credit_notes.length

              let receipt = Object.assign({}, data.receipts[i])
              receipt.model = 'receipt'
              receipt.gross = grossTotal > 0
              let created_for = receipt.gross ? 'multiple' : ''
              if (receipt.payment.invoice) {
                created_for = 'invoice'
              } else if (receipt.payment.credit_note) {
                created_for = 'credit_note'
              }
              receipt.created_for = created_for
              items.push(receipt)
            }

            for (let i in items) {
              let item = items[i]
              if (item.model !== 'receipt') {
                continue
              }
              this.statement.paid += item.created_for.model === 'credit_note' ? -item.payment.value : item.payment.value
            }

            items.sort((a, b) => {
              return this.$moment(a.created_at).diff(this.$moment(b.created_at))
            })

            this.$set(this.statement, 'items', items)
            resolve()
          })
          .catch(error => reject(error))
      })
    },
    print() {
      // we could store logodata in data()
      // however loading the logo on each print request ensures latest logo usage
      this.invoiceLogoData()
        .then((data) => {
          this._print(data)
        })
    },
    _print(invoiceLogoData) {
      let rows = []
      rows.push([
        {text: 'Date', bold: true},
        {text: 'Description', bold: true},
        {text: 'Total', bold: true},
        {text: 'Paid', bold: true},
        {text: 'Remaining', bold: true},
      ])

      if (this.statement.carried.total || this.statement.carried.paid || this.statement.carried.remaining) {
        rows.push([
          {text: 'Carried forward', colSpan: 2, fontSize: 11, bold: true},
          '',
          {text: this.$formatMoneyToFixed(this.statement.carried.total), fontSize: 11, bold: true},
          {text: this.$formatMoneyToFixed(this.statement.carried.paid), fontSize: 11, bold: true},
          {text: this.$formatMoneyToFixed(this.statement.carried.remaining), fontSize: 11, bold: true},
        ])
      }

      for (let i in this.statement.items) {
        let item = this.statement.items[i]
        if (item.model === 'receipt' && !this.showReceipts) {
          continue
        }
        let description = this.$options.filters.sentenceCase(item.model.replace('_', ' ')) + ' #' + item.id

        let total = '-'
        let paid = 0
        let remaining = '-'
        if (item.model === 'receipt') {
          paid = item.payment.value
          let created_for = ''
          if (item.payment.invoice) {
            created_for += item.payment.invoice.type + ' invoice #' + item.payment.invoice.id + '\n'
          }
          if (item.payment.credit_note) {
            created_for += item.payment.credit_note.type + ' credit note #' + item.payment.credit_note.id + '\n'
          }
          if (item.invoices.length) {
            let invoiceIDs = []
            for (let i = 0; i < item.invoices.length; i++) {
              invoiceIDs.push('#' + item.invoices[i].id)
            }
            created_for += item.invoices[0].type + ' invoices: [' + invoiceIDs.join(', ') + ']\n'
          }
          if (item.credit_notes.length) {
            let creditNoteIDs = []
            for (let i = 0; i < item.credit_notes.length; i++) {
              creditNoteIDs.push('#' + item.credit_notes[i].id)
            }
            created_for += item.credit_notes[0].type + ' credit notes: [' + creditNoteIDs.join(', ') + ']\n'
          }
          description += ` for\n${created_for}`
        } else {
          total = this.$formatMoney(item.total.sub - item.total.discount + item.total.tax)
          paid = item.total.paid
          remaining = this.$formatMoneyToFixed(item.total.remaining)
        }
        rows.push([
          item.created_at,
          description,
          total,
          this.$formatMoneyToFixed(paid),
          remaining,
        ])
      }

      rows.push([
        {text: 'Totals', colSpan: 2, fontSize: 11, bold: true},
        '',
        {text: this.$formatMoneyToFixed(Math.round(this.statement.carried.total + this.total)), fontSize: 11, bold: true},
        {text: this.$formatMoneyToFixed(Math.round(this.statement.carried.paid + this.statement.paid)), fontSize: 11, bold: true},
        {text: this.$formatMoneyToFixed(Math.round(this.statement.carried.remaining + this.totalRemaining)), fontSize: 11, bold: true},
      ])


      let addresses = {width: '*', text: ''}
      let addressList = []
      for (let i in this.contact.addresses) {
        let addr = this.contact.addresses[i]
        addressList.push(addr.address_1 + ',\n' + addr.city + ', ' + addr.state + ', ' + addr.post_code + ',\n' + this.CountryByID(addr.country_id).name)
      }
      addresses.text = addressList.join('\n')

      let phoneNumbers = {width: '*', text: ''}
      let phoneNumberList = []
      for (let i in this.contact.phone_numbers) {
        let phone = this.contact.phone_numbers[i]
        phoneNumberList.push('+' + phone.countryData.calling_code + ' ' + phone.number)
      }
      phoneNumbers.text = phoneNumberList.join('\n')

      let companyVATText = [
        { text: '\nVAT: ', fontSize: 9, bold: true },
        { text: this.config.companyVATNumber, fontSize: 9 },
      ]

      var docDefinition = {
        content: [
          {
            columns: [
              {
                width: 90,
                image: invoiceLogoData,
              },
              {
                width: '*',
                text: '',
              },
              {
                width: '50%',
                text: 'Statement of account',
                fontSize: 20,
                bold: true,
                alignment: 'right'
              }
            ],
          },
          {
            columns: [
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: [
                  this.dateRange.value,
                ],
                alignment: 'right'
              }
            ],
            margin: [ 0, 0, 0, 30 ],
          },
          {
            columns: [
              {
                text: [
                  {text: this.contact.first_name + ' ' + this.contact.last_name + (this.contact.company_name ? ' ' + this.contact.company_name : ''), bold: true, fontSize: 12},
                ]
              },
              {
                width: '*',
                text: '',
              },
            ],
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515 - 25*515/100, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 5, 0, 5 ]
          },
          {
            columns: [
              addresses,
              phoneNumbers,
              {
                width: '25%',
                text: [
                  { text: this.config.companyName + '\n', bold: true, fontSize: 10 },
                  { text: this.config.companyAddress + ' ' + this.config.companyCity + '\n', fontSize: 9 },
                  { text: (this.config.companyState ? this.config.companyState + ', ' : '') + ' ' + this.config.companyPostCode + ' ' + this.config.companyCountry + '\n', fontSize: 9 },
                  { text: this.config.companyPhone, fontSize: 9 },
                  ...this.config.companyVATNumber ? companyVATText : []
                ],
                alignment: 'right'
              }
            ],
            columnGap: 10
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 15, 0, 5 ]
          },
          {
            layout: 'lightHorizontalLines', // optional
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: ['auto', '*', 60, 60, 60],
              // widths: [ 'auto', '*', 'auto', 100, '*' ],
              body: [
                ...rows,
              ]
            },
            // left, top, right, bottom
            margin: [ 0, 20, 0, 20 ],
          },
          {
            columns: [
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Issuer\'s signature'],
                  ],
                }
              },
              {
                width: '*',
                text: ''
              },
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Receiver\'s signature'],
                  ],
                }
              },
            ],
          },
        ],
        footer(currentPage, pageCount) {
          if (pageCount === currentPage) {
            return {
              text: 'Thank you for doing business with us',
              alignment: 'center',
            }
          }
        },
        pageSize: 'A4',
        defaultStyle: {
          fontSize: 9,
          // bold: true
        }
      }

      pdfMake.createPdf(docDefinition).open()
    },
    invoiceLogoData() {
      return axios.get(this.config.logoPrintPath + '?_', {
        responseType: 'arraybuffer'
      })
        .then(response => {
          let image = btoa(
            new Uint8Array(response.data)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          )
          return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`
        })
    },
  }
}
</script>
